import React, { useEffect, useState } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Row,
    FormGroup,
    FormFeedback,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import Dropzone from "react-dropzone"
import DragListView from 'react-drag-listview'

import {
    getMerchantImages,
    manageMerchantImage,
    reorderMerchantImages,
    setLocalImagesOrder,
    resetLocalImagesOrder,
} from "store/merchants/actions"
import {
    convertFileToBase64,
    formatBytes,
    validateImageFile,
} from "helpers/file_helper"
import CButton from "components/Common/CButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import { uploadBase64File, getUserAuthorized } from "helpers/backend_helper"
import ConfirmButton from "components/Common/ConfirmButton"
import * as url from "../../helpers/url_helper";

// Image Preview Component
const ImagePreview = ({ image, onDelete, readOnly = false, showDragHandle = false }) => (
    <div style={{

        position: "relative",
        borderRadius: "8px",
        overflow: "hidden",
        display: "block"
    }}>
        <img
            src={image.image}
            alt="Image"
            height={200}
            style={{
                aspectRatio: "2 / 1",
                objectFit: "cover",
                cursor: "pointer"
            }}
            onClick={() => window.open(image.image, '_blank')}
        />

        {/* Drag Handle */}
        {!readOnly && showDragHandle && (
            <div className="drag-handle" style={{
                position: "absolute",
                top: "5px",
                left: "5px",
                background: "rgba(0,0,0,0.7)",
                color: "white",
                borderRadius: "3px",
                padding: "4px",
                cursor: "move",
                zIndex: 5
            }}>
                <i className="bx bx-move" style={{ fontSize: "14px" }}></i>
            </div>
        )}

        {/* Delete Button */}
        {!readOnly && (
            <div className="delete-button" style={{
                position: "absolute",
                top: "0px",
                right: "0px",
            }}>
                <ConfirmButton
                    contextUri={url.MANAGE_MERCHANT_IMAGE}
                    actionType="post"
                    validateBeforeConfirm={() => Promise.resolve(true)}
                    onConfirm={() => onDelete(image)}
                    message="Are you sure you want to delete this image? This action cannot be undone."
                    renderButton={
                        <button
                            style={{
                                background: "#f46a6a",
                                color: "white",
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                fontSize: "12px",
                                padding: "0",
                                cursor: "pointer"
                            }}
                            title="Delete Image"
                        >
                            ×
                        </button>
                    }
                />
            </div>
        )}
    </div>
)


export default function MerchantImageForm({
    page,
    match,
    history,
    activeTab,
    tabId,
    createdMerchantID,
}) {
    const [readOnly, setReadOnly] = useState(page === "detail")
    const [isUploading, setIsUploading] = useState(false)
    const [uploadError, setUploadError] = useState("")

    const dispatch = useDispatch()
    const merchantID = page === "add" ? createdMerchantID : match?.params?.id

    // styles for drag and drop
    useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            .react-drag-listview-ghost {
                opacity: 0.5;
                background: #007bff;
            }
            .react-drag-listview-chosen {
                opacity: 0.8;
            }
            .draggable-image-item.react-drag-listview-drag {
                transform: rotate(5deg);
                box-shadow: 0 5px 15px rgba(0,0,0,0.3);
            }
        `
        document.head.appendChild(style)

        return () => {
            document.head.removeChild(style)
        }
    }, [])

    const {
        merchantImages,
        localImages,
        hasOrderChanged,
        isReorderingImages,
        imageError,
        reorderError
    } = useSelector(state => ({
        merchantImages: state.merchants.merchantImages || [],
        localImages: state.merchants.localImages || [],
        hasOrderChanged: state.merchants.hasOrderChanged || false,
        isReorderingImages: state.merchants.isReorderingImages || false,
        reorderError: state.merchants.reorderError,
        imageError: state.merchants.imageError,
        isLoadingImages: state.merchants.isLoadingImages || false,
    }))

    // Fetch images only when tab is active
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (page === "detail" && merchantID && isTabActive) {
            dispatch(getMerchantImages({
                headers: { "input-parameters": { merchantID } },
            }))
        }
    }, [dispatch, merchantID, page, activeTab, tabId])

    // Authentication check function
    const checkAuthentication = async () => {
        try {
            const result = await getUserAuthorized({
                headers: {
                    "input-parameters": {
                        requestURI: url.MANAGE_MERCHANT_IMAGE,
                        requestMethod: "post",
                    },
                },
            })

            return result?.type === "USER_AUTHORIZATION_STATUS" &&
                result?.desc === "true" &&
                result?.code === "200"
        } catch (error) {
            return false
        }
    }

    const handleDropzoneClick = async (open) => {
        const isAuthorized = await checkAuthentication()
        if (isAuthorized) {
            open()
        }
    }

    const handleFileSelection = async (acceptedFiles, event) => {
        if (acceptedFiles.length > 0) {
            handleImageUpload(acceptedFiles)
        }
    }

    const handleImageUpload = async (files) => {
        if (!files.length || !merchantID) return

        setIsUploading(true)
        setUploadError("")
        const file = files[0]

        try {
            // Validate image file - square dimensions (550x550), 1MB limit
            await validateImageFile(file, "merchant", 1100, 550, 1)

            // Convert to base64
            const base64Img = await convertFileToBase64(file)

            // Prepare file object
            Object.assign(file, {
                preview: URL.createObjectURL(file),
                formattedSize: formatBytes(file.size),
                forExistingResource: false,
                base64encodedFileByteArray: base64Img,
                resourceType: "EUROPA_MERCHANT_IMAGE",
            })

            const uploadResponse = await uploadBase64File(file, {})

            if (uploadResponse?.fileName) {
                const payload = {
                    actionType: "create",
                    merchantID,
                    image: uploadResponse.fileName,
                }
                dispatch(manageMerchantImage(payload, {}, history))
            } else {
                throw new Error("Upload failed - no filename returned")
            }
        } catch (error) {
            console.error('Upload error:', error)
            setUploadError(error.message || "Upload failed")
        } finally {
            setIsUploading(false)
        }
    }

    // Handle image deletion
    const handleImageDelete = (imageToDelete) => {
        const payload = {
            actionType: "delete",
            merchantID,
            merchantImageID: imageToDelete.merchantImageID,
        }
        dispatch(manageMerchantImage(payload, {}, history))
    }

    // Handle drag end for reordering
    const handleDragEnd = (fromIndex, toIndex) => {
        if (fromIndex === toIndex) return

        const newImages = [...localImages]
        const [removed] = newImages.splice(fromIndex, 1)
        newImages.splice(toIndex, 0, removed)

        dispatch(setLocalImagesOrder(newImages))
    }

    // Handle reorder images API call
    const handleReorderImages = () => {
        if (!merchantID || !hasOrderChanged) return
        const reorderData = localImages.map((imageObj, index) => ({
            merchantImageID: imageObj.merchantImageID,
            displayOrder: index + 1,
            image: imageObj.imageName
        }))

        const payload = {
            merchantID,
            merchantImages: reorderData
        }

        dispatch(reorderMerchantImages(payload, {}, history))
    }

    // Handle cancel reorder
    const handleCancelReorder = () => {
        dispatch(resetLocalImagesOrder())
    }

    // Helper function to check if error should be displayed
    const shouldDisplayError = (error) => {
        if (!error) return false
        const errorMessage = error.message || error.description || ""
        const noDataPatterns = ["no content available"]
        return !noDataPatterns.some(pattern =>
            errorMessage.toLowerCase().includes(pattern)
        )
    }

    // Drag props for react-drag-listview
    const dragProps = {
        onDragEnd: handleDragEnd,
        nodeSelector: '.draggable-image-item',
        handleSelector: '.drag-handle',
        ignoreSelector: '.delete-button',
        enableScroll: false,
        lineClassName: 'drag-line',
    }

    return (
        <>
            {/* Error Display  */}
            {(imageError || reorderError) && shouldDisplayError(imageError || reorderError) && (
                <Alert color="danger" className="mb-3">
                    {imageError?.message || imageError?.description || reorderError?.message || reorderError?.description || "An error occurred"}
                </Alert>
            )}

            {/* Edit Mode */}
            {!readOnly && merchantID && (
                <Card className="mb-4">
                    <CardBody>
                        <h6 className="mb-3">
                            {page === "add" ? "Add Image" : (merchantImages.length > 0 ? "Edit Images" : "Add Image")}
                        </h6>

                        {/* Image Upload Section */}
                        <Row>
                            <Col md="6">
                                <AccessControlWrapper
                                    contextUri={url.MANAGE_MERCHANT_IMAGE}
                                    actionType="post"
                                >
                                    <FormGroup className="mb-3">
                                        {(isUploading || uploadError) ? (
                                            <>
                                                {isUploading && (
                                                    <div className="text-center p-4">
                                                        <div className="spinner-border text-primary mb-2" />
                                                        <p className="text-primary">Uploading...</p>
                                                    </div>
                                                )}
                                                {uploadError && (
                                                    <Dropzone
                                                        multiple={false}
                                                        accept={{
                                                            "image/*": [".png", ".jpeg", ".jpg"],
                                                        }}
                                                        onDrop={handleFileSelection}
                                                        disabled={isUploading}
                                                        noClick={true}
                                                    >
                                                        {({ getRootProps, getInputProps, open }) => (
                                                            <div className="dropzone">
                                                                <div
                                                                    className="dz-message needsclick mt-2"
                                                                    {...getRootProps()}
                                                                    onClick={(e) => {
                                                                        e.preventDefault()
                                                                        handleDropzoneClick(open)
                                                                    }}
                                                                >
                                                                    <input {...getInputProps()} />
                                                                    <div className="mb-3">
                                                                        <i className="display-4 text-muted bx bxs-cloud-upload" />
                                                                    </div>
                                                                    <h4>
                                                                        Drop image here or click to upload.
                                                                    </h4>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Dropzone>
                                                )}
                                            </>
                                        ) : (
                                            <Dropzone
                                                multiple={false}
                                                accept={{
                                                    "image/*": [".png", ".jpeg", ".jpg"],
                                                }}
                                                onDrop={handleFileSelection}
                                                disabled={isUploading}
                                                noClick={true}
                                            >
                                                {({ getRootProps, getInputProps, open }) => (
                                                    <div className="dropzone">
                                                        <div
                                                            className="dz-message needsclick mt-2"
                                                            {...getRootProps()}
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                handleDropzoneClick(open)
                                                            }}
                                                        >
                                                            <input {...getInputProps()} />
                                                            <div className="mb-3">
                                                                <i className="display-4 text-muted bx bxs-cloud-upload" />
                                                            </div>
                                                            <h4>
                                                                Drop image here or click to upload.
                                                            </h4>
                                                        </div>
                                                    </div>
                                                )}
                                            </Dropzone>
                                        )}

                                        {uploadError && (
                                            <FormFeedback type="invalid" className="d-block">
                                                {uploadError}
                                            </FormFeedback>
                                        )}

                                        <span className="text-muted text-end d-inline-block w-100">
                                            JPG & PNG are allowed up to 1 MB max (1100x550px)
                                        </span>
                                    </FormGroup>
                                </AccessControlWrapper>
                            </Col>
                        </Row>

                        {/* Existing Images Grid */}
                        {localImages.length > 0 && (
                            <Row className="mt-4">
                                <Col md="12">
                                    <div className="d-flex mb-3 gap-2">
                                        <h6 className="mb-0">Existing Images</h6>
                                        {localImages.length > 1 && (
                                            <small className="text-muted">
                                                (
                                                <i className="bx bx-move" />
                                                <span>Drag images to reorder</span>
                                                )
                                            </small>
                                        )}
                                    </div>

                                    <DragListView
                                        {...dragProps}
                                        disabled={localImages.length <= 1}
                                    >
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                            {localImages.map((image, index) => (
                                                <div
                                                    key={image.merchantImageID}
                                                    className="draggable-image-item"
                                                    style={{
                                                        // width: '100%',
                                                        position: 'relative',
                                                        border: hasOrderChanged ? "2px dashed #007bff" : "",
                                                        borderRadius: "8px",
                                                        padding: "2px",
                                                        transition: "all 0.3s ease",
                                                        cursor: localImages.length > 1 ? 'move' : 'default'
                                                    }}
                                                >
                                                    <ImagePreview
                                                        image={image}
                                                        onDelete={handleImageDelete}
                                                        showDragHandle={localImages.length > 1}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </DragListView>
                                </Col>
                            </Row>
                        )}

                        {/* Actions */}
                        <div className="text-end mt-3">
                            {hasOrderChanged ? (
                                <>
                                    <CButton.Secondary
                                        text="Cancel Reorder"
                                        onClick={handleCancelReorder}
                                        disabled={isReorderingImages}
                                        className="me-2"
                                    />

                                    <AccessControlWrapper
                                        contextUri={url.REORDER_MERCHANT_IMAGES}
                                        actionType="post"
                                    >
                                        <CButton.Primary
                                            text="Reorder Images"
                                            onClick={handleReorderImages}
                                            loading={isReorderingImages}
                                            disabled={isReorderingImages}
                                        />
                                    </AccessControlWrapper>
                                </>
                            ) : (
                                <CButton.Secondary
                                    text="Cancel"
                                    onClick={() => {
                                        setReadOnly(true)
                                        setUploadError("")
                                        dispatch(resetLocalImagesOrder())
                                    }}
                                    disabled={isUploading || isReorderingImages}
                                />
                            )}
                        </div>
                    </CardBody>
                </Card>
            )}

            {/* Read-only Gallery */}
            {readOnly && (
                <Card className="mb-4">
                    <CardBody>
                        {/* Edit/Add Button */}
                        {readOnly && (
                            <div className="mb-3 d-flex justify-content-between">
                                <h6>Images</h6>

                                <AccessControlWrapper
                                    contextUri={url.MANAGE_MERCHANT_IMAGE}
                                    actionType="post"
                                >
                                    {(() => {
                                        const ButtonComponent = merchantImages.length > 0 ? CButton.Primary : CButton.Add;
                                        return (
                                            <ButtonComponent
                                                text={merchantImages.length > 0 ? "Edit Images" : "Add Images"}
                                                icon={merchantImages.length > 0 ? "" : "bx bx-plus"}
                                                onClick={() => setReadOnly(false)}
                                                disabled={!merchantID}
                                            />
                                        );
                                    })()}
                                </AccessControlWrapper>
                            </div>
                        )}
                        {merchantImages.length === 0 ? (
                            <div className="text-center p-5">
                                <i className="bx bx-image display-4 text-muted mb-3"></i>
                                <p className="text-muted">Add images to showcase your merchant</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                {merchantImages.map((image) => (
                                    <div
                                        key={image.merchantImageID}
                                        // style={{
                                        //     width: 'calc(25% - 12px)',
                                        //     minWidth: '150px',
                                        //     maxWidth: '200px'
                                        // }}
                                    >
                                        <ImagePreview
                                            image={image}
                                            readOnly={true}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}
        </>
    )
}